import type {
  CalibrationPriority,
  CheckDisagreementDetail,
  CheckAnswer,
} from "@/services/api/agentReview";

export function isArtefactPhase(phase: string): boolean {
  return phase === "artefact";
}

export function phaseLabel(phase: string): string {
  if (phase === "orchestration") return "Orchestrate";
  if (phase === "output") return "Output";
  if (phase === "artefact") return "Artefacts";
  return phase;
}

export function checkDisagreementKey(phase: string, checkId: string): string {
  return `${phase}:${checkId}`;
}

export function answerLabel(answer: CheckAnswer): string {
  if (answer === "pass") return "Pass";
  if (answer === "fail") return "Fail";
  return "N/A";
}

export function disagreementsForPriority(
  priority: CalibrationPriority,
  disagreements: CheckDisagreementDetail[],
): CheckDisagreementDetail[] {
  const key = checkDisagreementKey(priority.phase, priority.check_id);
  return disagreements.filter(
    (item) => checkDisagreementKey(item.phase, item.check_id) === key,
  );
}

export function humanCommentSamples(
  priority: CalibrationPriority,
  disagreements: CheckDisagreementDetail[],
  limit = 3,
): string[] {
  const samples: string[] = [];
  const seen = new Set<string>();
  for (const item of disagreementsForPriority(priority, disagreements)) {
    const comment = item.human_comment?.trim();
    if (!comment || seen.has(comment)) continue;
    seen.add(comment);
    samples.push(comment);
    if (samples.length >= limit) break;
  }
  return samples;
}

export function disagreementForCase(
  priority: CalibrationPriority,
  reviewId: string,
  disagreements: CheckDisagreementDetail[],
  artifactId?: string | null,
): CheckDisagreementDetail | undefined {
  return disagreementsForPriority(priority, disagreements).find((item) => {
    if (item.review_id !== reviewId) return false;
    if (!artifactId) return true;
    return (item.artifact_id ?? "") === artifactId;
  });
}

export function disputeResolutionKey(
  item: Pick<CheckDisagreementDetail, "review_id" | "phase" | "check_id" | "artifact_id">,
): string {
  return `${item.review_id}:${item.phase}:${item.check_id}:${item.artifact_id ?? ""}`;
}

export function priorityResolutionKey(priority: Pick<CalibrationPriority, "phase" | "check_id">): string {
  return checkDisagreementKey(priority.phase, priority.check_id);
}

export type PriorityResolutionSummary =
  | { status: "open" }
  | { status: "partial"; resolvedCount: number; total: number }
  | {
      status: "fully_resolved";
      outcome: NonNullable<CheckDisagreementDetail["resolution_outcome"]>;
      note?: string;
    };

export function priorityResolutionSummary(
  priority: CalibrationPriority,
  disagreements: CheckDisagreementDetail[],
): PriorityResolutionSummary {
  const items = disagreementsForPriority(priority, disagreements);
  const resolved = items.filter((item) => item.resolution_outcome);
  const open = items.filter((item) => !item.resolution_outcome);

  if (resolved.length === 0) {
    return { status: "open" };
  }
  if (open.length > 0) {
    return { status: "partial", resolvedCount: resolved.length, total: items.length };
  }

  const outcomes = new Set(resolved.map((item) => item.resolution_outcome));
  const outcome = outcomes.size === 1 ? resolved[0].resolution_outcome : undefined;
  if (!outcome) {
    return { status: "partial", resolvedCount: resolved.length, total: items.length };
  }

  const note = resolved.find((item) => item.resolution_note?.trim())?.resolution_note;
  return { status: "fully_resolved", outcome, note };
}

export function priorityBulkResolvedSummary(
  priority: CalibrationPriority,
  disagreements: CheckDisagreementDetail[],
):
  | { status: "open" }
  | { status: "all_closed"; note?: string }
  | { status: "partial_closed"; closedCount: number; total: number } {
  const items = disagreementsForPriority(priority, disagreements);
  const closed = items.filter((item) => item.resolution_outcome === "resolved");
  if (closed.length === 0) {
    return { status: "open" };
  }
  if (closed.length === items.length) {
    const note = closed.find((item) => item.resolution_note?.trim())?.resolution_note;
    return { status: "all_closed", note };
  }
  return { status: "partial_closed", closedCount: closed.length, total: items.length };
}

export function representativeDispute(
  priority: CalibrationPriority,
  disagreements: CheckDisagreementDetail[],
): CheckDisagreementDetail | undefined {
  const items = disagreementsForPriority(priority, disagreements);
  if (items.length === 0) return undefined;

  const dominantPair = priority.answer_pairs[0];
  if (dominantPair) {
    const match = items.find(
      (item) =>
        item.human_answer === dominantPair.human_answer &&
        item.ace_answer === dominantPair.ace_answer,
    );
    if (match) return match;
  }
  return items[0];
}

export function caseDisplayLabel(item: Pick<CheckDisagreementDetail, "subject_label" | "artifact_title">): string {
  if (item.artifact_title?.trim()) {
    return item.artifact_title.trim();
  }
  return item.subject_label?.trim() || "Case";
}
